import { z } from "zod";

const SHA256 = /^sha256:[a-f0-9]{64}$/;
const contentRefSchema = z
  .object({ id: z.string().min(1), contentHash: z.string().regex(SHA256) })
  .strict();
const capabilityGrantSchema = z
  .object({
    localEvaluation: z.boolean(),
    ontologyExtraction: z.boolean(),
    packDerivation: z.boolean(),
    derivativePublication: z.boolean(),
    rawRedistribution: z.boolean(),
    upstreamWriteback: z.boolean(),
  })
  .strict();
const attributionSchema = z
  .object({
    required: z.boolean(),
    text: z.string().min(1).optional(),
    sourceUrl: z.string().min(1).optional(),
    licenseId: z.string().min(1).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (
      value.required &&
      (!value.text || !value.sourceUrl || !value.licenseId)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "required attribution must retain text, sourceUrl, and licenseId",
      });
    }
    if (
      !value.required &&
      (value.text !== undefined ||
        value.sourceUrl !== undefined ||
        value.licenseId !== undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "non-required attribution must not carry inherited fields",
      });
    }
  });

export type SourceStructureSelectionMemberV1 = {
  kind: "openpencil-source-structure-member-v1";
  sourceId: string;
  structuralPath: string[];
  sourceFactHash: string;
};
export type ClosedSourceStructuralPathV1 =
  | [pageName: string]
  | [pageName: string, axis: "componentSets", componentName: string]
  | [namespace: "variables", axis: "collections", collectionName: string];
export type SourceStructureSelectionV1 = {
  kind: "openpencil-source-structure-selection-v1";
  selectionId: string;
  members: SourceStructureSelectionMemberV1[];
  canonicalSelectionHash: string;
};
export type OpenPencilSourceStructureSelectionRequest =
  | readonly string[]
  | {
      structuralPath: readonly string[];
      sourceFactHash: string;
    };
export type OpenPencilSourceStructureFact = {
  structuralPath: string[];
  sourceFactHash: string;
};

const normalizedPathSegmentSchema = z.string().min(1);
const sourceStructuralPathV1Schema = z.union([
  z.tuple([normalizedPathSegmentSchema]),
  z.tuple([
    normalizedPathSegmentSchema,
    z.literal("componentSets"),
    normalizedPathSegmentSchema,
  ]),
  z.tuple([
    z.literal("variables"),
    z.literal("collections"),
    normalizedPathSegmentSchema,
  ]),
]);

export const SourceStructureSelectionV1Schema = z
  .object({
    kind: z.literal("openpencil-source-structure-selection-v1"),
    selectionId: z.string().min(1),
    members: z
      .array(
        z
          .object({
            kind: z.literal("openpencil-source-structure-member-v1"),
            sourceId: z.string().regex(/^source-structure:[a-f0-9]{64}$/),
            structuralPath: sourceStructuralPathV1Schema,
            sourceFactHash: z.string().regex(SHA256),
          })
          .strict(),
      )
      .min(1),
    canonicalSelectionHash: z.string().regex(SHA256),
  })
  .strict()
  .superRefine((value, ctx) => {
    for (const [index, member] of value.members.entries()) {
      const normalized = normalizePath(member.structuralPath);
      if (
        JSON.stringify(member.structuralPath) !== JSON.stringify(normalized)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["members", index, "structuralPath"],
          message: "structuralPath must be NFKC-normalized and trimmed",
        });
      }
      if (index > 0) {
        if (compareSelectionMembers(value.members[index - 1]!, member) >= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["members", index],
            message: "members must be in canonical order and unique",
          });
        }
      }
    }
  });

export interface SelectionResolutionResult {
  passed: boolean;
  errors: string[];
}
export interface CapabilityEvaluation {
  passed: boolean;
  requiredCapabilities: CapabilityName[];
  missingCapabilities: CapabilityName[];
  errors: string[];
}
export interface AttributionEvaluation {
  passed: boolean;
  errors: string[];
}
export interface ExtractionBoundaryEvaluation {
  passed: boolean;
  errors: string[];
  rejectedJsonPointers: string[];
}
export interface ReceiptEvaluation {
  passed: boolean;
  errors: string[];
}
export type CapabilityName =
  z.infer<typeof capabilityGrantSchema> extends Record<infer Key, boolean>
    ? Extract<Key, string>
    : never;

type ReadbackFact = Record<string, unknown>;
type StructuralReadback = {
  variables?: { collectionSamples?: ReadbackFact[] };
  pages?: { samples?: ReadbackFact[] };
  uiOntology?: { componentSets?: ReadbackFact[] };
};

export interface CanonicalVariableCollectionSelectionFactV1 {
  kind: "openpencil-variable-collection-selection-fact.v1";
  collectionName: string;
  modes: Array<{
    modeName: string;
    isDefault: boolean | null;
  }>;
}

const issuedLegacyLocatorBoundSelections = new Map<string, string>([
  [
    "selection.material3.foundation-building-blocks.v1",
    "sha256:b72397fedbeb21c2c4e34a2b98d4e5341475343734330a36a28cc2f0aff2ec65",
  ],
  [
    "selection.flowbite.foundation-feedback-forms.v1",
    "sha256:4528442e567d6e2122e0b0332981427fbe4ee43779290468dfa784efb87ef30c",
  ],
]);

const operationRequirements = {
  localEvaluation: ["localEvaluation"],
  ontologyCandidate: ["localEvaluation", "ontologyExtraction"],
  packRelease: ["ontologyExtraction", "packDerivation"],
  privateCanary: ["ontologyExtraction", "packDerivation"],
  publicDerivative: [
    "ontologyExtraction",
    "packDerivation",
    "derivativePublication",
  ],
  rawDistribution: ["rawRedistribution"],
  upstreamWriteback: ["upstreamWriteback"],
} as const satisfies Record<string, readonly CapabilityName[]>;

function normalizeString(value: string): string {
  const normalized = value.normalize("NFKC").trim();
  if (!normalized) throw new Error("ADMISSION_SELECTION_PATH_EMPTY");
  return normalized;
}

function normalizePath(path: readonly string[]): ClosedSourceStructuralPathV1 {
  if (!Array.isArray(path) || path.length === 0)
    throw new Error("ADMISSION_SELECTION_PATH_INVALID");
  const normalized = path.map((segment) => {
    if (typeof segment !== "string")
      throw new Error("ADMISSION_SELECTION_PATH_INVALID");
    return normalizeString(segment);
  });
  const parsed = sourceStructuralPathV1Schema.safeParse(normalized);
  if (!parsed.success) throw new Error("ADMISSION_SELECTION_PATH_INVALID");
  return parsed.data;
}

function canonicalize(value: unknown, seen = new WeakSet<object>()): unknown {
  if (value === null || typeof value === "string" || typeof value === "boolean")
    return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value))
      throw new Error("ADMISSION_CANONICAL_JSON_NON_FINITE_NUMBER");
    return value;
  }
  if (Array.isArray(value))
    return value.map((item) => canonicalize(item, seen));
  if (typeof value !== "object" || value === undefined)
    throw new Error("ADMISSION_CANONICAL_JSON_UNSUPPORTED_VALUE");
  if (seen.has(value)) throw new Error("ADMISSION_CANONICAL_JSON_CYCLE");
  seen.add(value);
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(value as Record<string, unknown>).sort()) {
    const item = (value as Record<string, unknown>)[key];
    if (item !== undefined) result[key] = canonicalize(item, seen);
  }
  seen.delete(value);
  return result;
}

/** Deterministic JSON for content-addressed v2 admission artifacts. */
export function stableAdmissionJson(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

/** Hashes only the supplied artifact, excluding its top-level self-reference. */
export async function admissionContentHash(
  valueWithoutContentHash: unknown,
): Promise<string> {
  const base =
    valueWithoutContentHash &&
    typeof valueWithoutContentHash === "object" &&
    !Array.isArray(valueWithoutContentHash)
      ? Object.fromEntries(
          Object.entries(
            valueWithoutContentHash as Record<string, unknown>,
          ).filter(([key]) => key !== "contentHash"),
        )
      : valueWithoutContentHash;
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(stableAdmissionJson(base)),
  );
  return `sha256:${[...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

function jsonPointer(path: readonly (string | number)[]): string {
  return path.length === 0
    ? "/"
    : `/${path.map((part) => String(part).replace(/~/g, "~0").replace(/\//g, "~1")).join("/")}`;
}

function factWithoutLocalIdentity(
  value: ReadbackFact,
  parseOrPathLocalIdentity: RegExp,
): Record<string, unknown> {
  const strip = (item: unknown): unknown => {
    if (Array.isArray(item)) return item.map(strip);
    if (!item || typeof item !== "object") return item;
    return Object.fromEntries(
      Object.entries(item as Record<string, unknown>)
        .filter(([key]) => !parseOrPathLocalIdentity.test(key))
        .map(([key, child]) => [key, strip(child)]),
    );
  };
  return strip(value) as Record<string, unknown>;
}

function stableFact(value: ReadbackFact): Record<string, unknown> {
  return factWithoutLocalIdentity(
    value,
    /^(?:source)?nodeId$|^graphNodeId$|^figmaNodeId$|^sourceLocator$/i,
  );
}

function legacyStableFact(value: ReadbackFact): Record<string, unknown> {
  return factWithoutLocalIdentity(
    value,
    /^(?:source)?nodeId$|^graphNodeId$|^figmaNodeId$/i,
  );
}

function compareUtf8(left: string, right: string): number {
  const encoder = new TextEncoder();
  const a = encoder.encode(left);
  const b = encoder.encode(right);
  for (let index = 0; index < Math.min(a.length, b.length); index += 1) {
    if (a[index] !== b[index]) return a[index]! - b[index]!;
  }
  return a.length - b.length;
}

export function canonicalVariableCollectionSelectionFactV1(
  value: ReadbackFact,
): CanonicalVariableCollectionSelectionFactV1 {
  const collectionName =
    typeof value.name === "string"
      ? normalizeString(value.name)
      : (() => {
          throw new Error("ADMISSION_VARIABLE_COLLECTION_NAME_INVALID");
        })();
  if (!Array.isArray(value.modes)) {
    throw new Error("ADMISSION_VARIABLE_COLLECTION_MODES_INVALID");
  }
  const modes = value.modes
    .map((mode) => {
      if (!mode || typeof mode !== "object" || Array.isArray(mode)) {
        throw new Error("ADMISSION_VARIABLE_COLLECTION_MODE_INVALID");
      }
      const raw = mode as Record<string, unknown>;
      const modeName =
        typeof raw.name === "string"
          ? normalizeString(raw.name)
          : (() => {
              throw new Error(
                "ADMISSION_VARIABLE_COLLECTION_MODE_NAME_INVALID",
              );
            })();
      const isDefault = raw.isDefault === undefined ? null : raw.isDefault;
      if (isDefault !== null && typeof isDefault !== "boolean") {
        throw new Error("ADMISSION_VARIABLE_COLLECTION_DEFAULT_MODE_INVALID");
      }
      return { modeName, isDefault };
    })
    .sort((left, right) => compareUtf8(left.modeName, right.modeName));
  if (new Set(modes.map((mode) => mode.modeName)).size !== modes.length) {
    throw new Error("ADMISSION_VARIABLE_COLLECTION_MODE_NAME_DUPLICATE");
  }
  if (modes.filter((mode) => mode.isDefault === true).length > 1) {
    throw new Error("ADMISSION_VARIABLE_COLLECTION_DEFAULT_MODE_AMBIGUOUS");
  }
  return {
    kind: "openpencil-variable-collection-selection-fact.v1",
    collectionName,
    modes,
  };
}

function factsForPath(
  readback: StructuralReadback,
  path: readonly string[],
): ReadbackFact[] {
  if (path.length === 1) {
    return (readback.pages?.samples ?? []).filter(
      (fact) =>
        typeof fact.name === "string" && normalizeString(fact.name) === path[0],
    );
  }
  if (path.length === 3 && path[1] === "componentSets") {
    return (readback.uiOntology?.componentSets ?? []).filter(
      (fact) =>
        typeof fact.pageName === "string" &&
        typeof fact.name === "string" &&
        normalizeString(fact.pageName) === path[0] &&
        normalizeString(fact.name) === path[2],
    );
  }
  if (
    path.length === 3 &&
    path[0] === "variables" &&
    path[1] === "collections"
  ) {
    return (readback.variables?.collectionSamples ?? []).filter(
      (fact) =>
        typeof fact.name === "string" && normalizeString(fact.name) === path[2],
    );
  }
  return [];
}

function selectionFact(
  structuralPath: readonly string[],
  fact: ReadbackFact,
): Record<string, unknown> | CanonicalVariableCollectionSelectionFactV1 {
  return structuralPath.length === 3 &&
    structuralPath[0] === "variables" &&
    structuralPath[1] === "collections"
    ? canonicalVariableCollectionSelectionFactV1(fact)
    : stableFact(fact);
}

async function selectionMember(
  pathInput: readonly string[],
  fact: ReadbackFact,
): Promise<SourceStructureSelectionMemberV1> {
  const structuralPath = normalizePath(pathInput);
  const identity = {
    kind: "openpencil-source-structure-member-v1" as const,
    structuralPath,
  };
  return {
    ...identity,
    sourceId: `source-structure:${(await admissionContentHash(identity)).slice("sha256:".length)}`,
    sourceFactHash: await admissionContentHash({
      ...identity,
      fact: selectionFact(structuralPath, fact),
    }),
  };
}

export async function listOpenPencilSourceStructureFacts(
  readback: StructuralReadback,
  pathInput: readonly string[],
): Promise<OpenPencilSourceStructureFact[]> {
  const structuralPath = normalizePath(pathInput);
  return Promise.all(
    factsForPath(readback, structuralPath).map(async (fact) => ({
      structuralPath,
      sourceFactHash: (await selectionMember(structuralPath, fact))
        .sourceFactHash,
    })),
  );
}

function compareSelectionMembers(
  left: SourceStructureSelectionMemberV1,
  right: SourceStructureSelectionMemberV1,
): number {
  const leftPath = stableAdmissionJson(left.structuralPath);
  const rightPath = stableAdmissionJson(right.structuralPath);
  if (leftPath < rightPath) return -1;
  if (leftPath > rightPath) return 1;
  if (left.sourceFactHash < right.sourceFactHash) return -1;
  if (left.sourceFactHash > right.sourceFactHash) return 1;
  return 0;
}

function requestedPath(request: OpenPencilSourceStructureSelectionRequest): {
  path: string[];
  sourceFactHash?: string;
} {
  if (Array.isArray(request)) return { path: normalizePath(request) };
  const requested = request as Exclude<
    OpenPencilSourceStructureSelectionRequest,
    readonly string[]
  >;
  if (
    !requested ||
    typeof requested !== "object" ||
    !Array.isArray(requested.structuralPath) ||
    typeof requested.sourceFactHash !== "string" ||
    !SHA256.test(requested.sourceFactHash)
  ) {
    throw new Error("ADMISSION_SELECTION_REQUEST_INVALID");
  }
  return {
    path: normalizePath(requested.structuralPath),
    sourceFactHash: requested.sourceFactHash,
  };
}

export async function buildOpenPencilSourceStructureSelection(
  readback: StructuralReadback,
  structuralPaths: readonly OpenPencilSourceStructureSelectionRequest[],
  selectionId: string,
): Promise<SourceStructureSelectionV1> {
  const members = await Promise.all(
    structuralPaths.map(async (request) => {
      const { path, sourceFactHash } = requestedPath(request);
      const facts = factsForPath(readback, path);
      const candidates = await Promise.all(
        facts.map((fact) => selectionMember(path, fact)),
      );
      const matches = sourceFactHash
        ? candidates.filter(
            (candidate) => candidate.sourceFactHash === sourceFactHash,
          )
        : candidates;
      if (matches.length !== 1)
        throw new Error(
          `ADMISSION_SELECTION_PATH_MUST_RESOLVE_EXACTLY_ONCE: path must resolve exactly one fact: ${JSON.stringify(path)}`,
        );
      return matches[0]!;
    }),
  );
  const sortedMembers = [...members].sort(compareSelectionMembers);
  const base = {
    kind: "openpencil-source-structure-selection-v1" as const,
    selectionId: normalizeString(selectionId),
    members: sortedMembers,
  };
  return SourceStructureSelectionV1Schema.parse({
    ...base,
    canonicalSelectionHash: await admissionContentHash(base),
  });
}

export function resolveOpenPencilSourceStructureSelection(
  readback: StructuralReadback,
  selectionInput: unknown,
): SelectionResolutionResult {
  const selection = SourceStructureSelectionV1Schema.safeParse(selectionInput);
  if (!selection.success)
    return {
      passed: false,
      errors: selection.error.issues.map(
        (issue) => `selection:${issue.path.join(".")}:${issue.message}`,
      ),
    };
  const errors: string[] = [];
  const acceptsLegacyLocatorBoundFacts =
    issuedLegacyLocatorBoundSelections.get(selection.data.selectionId) ===
    selection.data.canonicalSelectionHash;
  for (const member of selection.data.members) {
    let facts: ReadbackFact[];
    try {
      facts = factsForPath(readback, member.structuralPath);
    } catch {
      errors.push(
        `selection ${JSON.stringify(member.structuralPath)} source fact is invalid`,
      );
      continue;
    }
    // The small synchronous evaluator still verifies source identity. Fact hashing is
    // asynchronous Web Crypto, so the async build path remains the authoritative
    // construction boundary and callers can compare its recomputed selection.
    const expectedSourceId = `source-structure:${stableHashSync({ kind: member.kind, structuralPath: member.structuralPath })}`;
    if (member.sourceId !== expectedSourceId)
      errors.push(
        `selection ${JSON.stringify(member.structuralPath)} sourceId does not match structural identity`,
      );
    let matchingFacts: ReadbackFact[];
    try {
      matchingFacts = facts.filter((fact) => {
        const identity = {
          kind: member.kind,
          structuralPath: member.structuralPath,
        };
        const currentHash = `sha256:${stableHashSync({
          ...identity,
          fact: selectionFact(member.structuralPath, fact),
        })}`;
        const legacyHash = `sha256:${stableHashSync({
          ...identity,
          fact: legacyStableFact(fact),
        })}`;
        return (
          member.sourceFactHash === currentHash ||
          (acceptsLegacyLocatorBoundFacts &&
            member.sourceFactHash === legacyHash)
        );
      });
    } catch {
      errors.push(
        `selection ${JSON.stringify(member.structuralPath)} source fact is invalid`,
      );
      continue;
    }
    if (matchingFacts.length !== 1)
      errors.push(
        `selection ${JSON.stringify(member.structuralPath)} sourceFactHash changed or is ambiguous`,
      );
  }
  const canonicalBase = {
    kind: selection.data.kind,
    selectionId: selection.data.selectionId,
    members: selection.data.members,
  };
  if (
    selection.data.canonicalSelectionHash !==
    `sha256:${stableHashSync(canonicalBase)}`
  )
    errors.push("selection canonicalSelectionHash changed");
  return { passed: errors.length === 0, errors };
}

// Web Crypto is async; selection resolution is deliberately synchronous for
// schema validators. This tiny SHA-256 implementation keeps both paths byte-equal.
function stableHashSync(value: unknown): string {
  const bytes = new TextEncoder().encode(stableAdmissionJson(value));
  const words: number[] = [];
  for (let offset = 0; offset < bytes.length; offset += 4)
    words.push(
      ((bytes[offset] ?? 0) << 24) |
        ((bytes[offset + 1] ?? 0) << 16) |
        ((bytes[offset + 2] ?? 0) << 8) |
        (bytes[offset + 3] ?? 0),
    );
  const bitLength = bytes.length * 8;
  words[Math.floor(bytes.length / 4)] =
    (words[Math.floor(bytes.length / 4)] ?? 0) |
    (0x80 << (24 - (bytes.length % 4) * 8));
  while (words.length % 16 !== 14) words.push(0);
  words.push(Math.floor(bitLength / 0x100000000), bitLength >>> 0);
  let [a, b, c, d, e, f, g, h] = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
    0x1f83d9ab, 0x5be0cd19,
  ];
  const k = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
    0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
    0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
    0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
    0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
    0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];
  const r = (x: number, n: number) => (x >>> n) | (x << (32 - n));
  for (let offset = 0; offset < words.length; offset += 16) {
    const w = words.slice(offset, offset + 16);
    for (let i = 16; i < 64; i++)
      w[i] =
        ((r(w[i - 2]!, 17) ^ r(w[i - 2]!, 19) ^ (w[i - 2]! >>> 10)) +
          w[i - 7]! +
          (r(w[i - 15]!, 7) ^ r(w[i - 15]!, 18) ^ (w[i - 15]! >>> 3)) +
          w[i - 16]!) |
        0;
    let [aa, bb, cc, dd, ee, ff, gg, hh] = [a, b, c, d, e, f, g, h];
    for (let i = 0; i < 64; i++) {
      const s1 = r(ee, 6) ^ r(ee, 11) ^ r(ee, 25),
        ch = (ee & ff) ^ (~ee & gg),
        t1 = (hh + s1 + ch + k[i]! + w[i]!) | 0,
        s0 = r(aa, 2) ^ r(aa, 13) ^ r(aa, 22),
        maj = (aa & bb) ^ (aa & cc) ^ (bb & cc),
        t2 = (s0 + maj) | 0;
      hh = gg;
      gg = ff;
      ff = ee;
      ee = (dd + t1) | 0;
      dd = cc;
      cc = bb;
      bb = aa;
      aa = (t1 + t2) | 0;
    }
    a = (a + aa) | 0;
    b = (b + bb) | 0;
    c = (c + cc) | 0;
    d = (d + dd) | 0;
    e = (e + ee) | 0;
    f = (f + ff) | 0;
    g = (g + gg) | 0;
    h = (h + hh) | 0;
  }
  return [a, b, c, d, e, f, g, h]
    .map((word) => (word >>> 0).toString(16).padStart(8, "0"))
    .join("");
}

export function evaluateAdmissionCapability(
  operation: string,
  grant: unknown,
): CapabilityEvaluation {
  const parsed = capabilityGrantSchema.safeParse(grant);
  const requiredCapabilities =
    operationRequirements[operation as keyof typeof operationRequirements];
  if (!parsed.success)
    return {
      passed: false,
      requiredCapabilities: [],
      missingCapabilities: [],
      errors: ["capability grant is not the closed six-capability object"],
    };
  if (!requiredCapabilities)
    return {
      passed: false,
      requiredCapabilities: [],
      missingCapabilities: [],
      errors: [`unknown admission operation: ${operation}`],
    };
  const missingCapabilities = requiredCapabilities.filter(
    (capability) => !parsed.data[capability],
  );
  return {
    passed: missingCapabilities.length === 0,
    requiredCapabilities: [...requiredCapabilities],
    missingCapabilities,
    errors: missingCapabilities.map(
      (capability) => `missing required capability: ${capability}`,
    ),
  };
}

export function evaluateAttributionInheritance(
  sourceObligations: unknown,
  outputObligations: unknown,
): AttributionEvaluation {
  const sources = z.array(attributionSchema).safeParse(sourceObligations);
  const outputs = z.array(attributionSchema).safeParse(outputObligations);
  if (!sources.success || !outputs.success)
    return { passed: false, errors: ["attribution obligations are invalid"] };
  const errors: string[] = [];
  for (const source of sources.data.filter(
    (obligation) => obligation.required,
  )) {
    const inherited = outputs.data.some(
      (output) =>
        output.required &&
        output.text === source.text &&
        output.sourceUrl === source.sourceUrl &&
        output.licenseId === source.licenseId,
    );
    if (!inherited)
      errors.push(
        `required attribution was not inherited byte-for-byte: ${source.sourceUrl}`,
      );
  }
  return { passed: errors.length === 0, errors };
}

const prohibitedExtractionKeys = new Set([
  "rasterBytes",
  "vectorBytes",
  "vectorPath",
  "rawFigBytes",
  "geometryReplica",
  "nodeId",
  "graphNodeId",
  "figmaNodeId",
]);
const extractionAllowedClasses = [
  "normalized-token-values",
  "semantic-token-roles",
  "component-roles-slots-variants-states",
  "accessibility-and-interaction-affordances",
  "layout-constraints-and-structure-patterns",
  "source-to-cogo-role-mappings",
] as const;
const extractionProhibitedClasses = [
  "raster-asset-bytes",
  "vector-asset-bytes-or-paths",
  "raw-fig-bytes",
  "exact-geometry-replica",
  "unbounded-source-text-corpus",
  "parse-unstable-node-identities",
  "unadmitted-behavior-or-business-rules",
] as const;
const extractionProfileSchema = z
  .object({
    profileId: z.literal("cogo.read-only-design-ontology.v1"),
    allowedClasses: z
      .array(z.enum(extractionAllowedClasses))
      .min(1)
      .max(6)
      .superRefine((value, ctx) => {
        if (new Set(value).size !== value.length)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "allowedClasses must be unique",
          });
      }),
    prohibitedClasses: z
      .array(z.enum(extractionProhibitedClasses))
      .length(extractionProhibitedClasses.length)
      .superRefine((value, ctx) => {
        if (new Set(value).size !== value.length)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "prohibitedClasses must be complete and unique",
          });
      }),
    assetBytesMayLeaveVault: z.literal(false),
  })
  .strict();
export function evaluateReadOnlyOntologyExtraction(
  candidate: unknown,
  profile: unknown,
): ExtractionBoundaryEvaluation {
  const parsedProfile = extractionProfileSchema.safeParse(profile);
  const rejectedJsonPointers: string[] = [];
  const errors: string[] = [];
  const candidateRecord = record(candidate);
  const fieldClasses: Record<
    string,
    (typeof extractionAllowedClasses)[number]
  > = {
    normalizedTokenValues: "normalized-token-values",
    semanticRoles: "semantic-token-roles",
    componentRoles: "component-roles-slots-variants-states",
    variants: "component-roles-slots-variants-states",
    states: "component-roles-slots-variants-states",
    slots: "component-roles-slots-variants-states",
    accessibility: "accessibility-and-interaction-affordances",
    layoutConstraints: "layout-constraints-and-structure-patterns",
    structurePatterns: "layout-constraints-and-structure-patterns",
    sourceToCogoRoleMappings: "source-to-cogo-role-mappings",
  };
  if (!candidateRecord) errors.push("ontology candidate must be a JSON object");
  if (candidateRecord && parsedProfile.success) {
    for (const key of Object.keys(candidateRecord)) {
      if (key === "sourceRefs" || key === "confidence") continue;
      const extractionClass = fieldClasses[key];
      if (!extractionClass)
        errors.push(`ontology candidate field is not allowlisted: ${key}`);
      else if (!parsedProfile.data.allowedClasses.includes(extractionClass))
        errors.push(`extraction profile does not allow ${extractionClass}`);
    }
    const sourceRefs = candidateRecord.sourceRefs;
    if (
      !Array.isArray(sourceRefs) ||
      sourceRefs.length === 0 ||
      sourceRefs.some((ref) => !contentRefSchema.safeParse(ref).success)
    ) {
      errors.push("ontology candidate requires pinned sourceRefs");
    }
  }
  const inspect = (value: unknown, path: (string | number)[]): void => {
    if (Array.isArray(value)) {
      value.forEach((item, index) => inspect(item, [...path, index]));
      return;
    }
    if (!value || typeof value !== "object") return;
    for (const [key, child] of Object.entries(
      value as Record<string, unknown>,
    )) {
      const childPath = [...path, key];
      if (prohibitedExtractionKeys.has(key))
        rejectedJsonPointers.push(jsonPointer(childPath));
      inspect(child, childPath);
    }
  };
  inspect(candidate, []);
  rejectedJsonPointers.sort();
  if (!parsedProfile.success)
    errors.push(
      "extraction profile must keep asset bytes in the vault and declare allowed classes",
    );
  errors.push(
    ...rejectedJsonPointers.map(
      (pointer) => `prohibited extraction field at ${pointer}`,
    ),
  );
  return { passed: errors.length === 0, errors, rejectedJsonPointers };
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}
async function hashErrors(label: string, value: unknown): Promise<string[]> {
  const artifact = record(value);
  if (!artifact || typeof artifact.contentHash !== "string")
    return [`${label} is missing contentHash`];
  return artifact.contentHash === (await admissionContentHash(artifact))
    ? []
    : [`${label} contentHash does not match canonical content`];
}
function sameRef(
  ref: unknown,
  id: unknown,
  hash: unknown,
  label: string,
): string[] {
  const parsed = contentRefSchema.safeParse(ref);
  return parsed.success &&
    parsed.data.id === id &&
    parsed.data.contentHash === hash
    ? []
    : [`${label} is not an exact id/contentHash binding`];
}
const sourceIdentitySchema = z.union([
  z
    .object({
      kind: z.literal("local-figma-snapshot"),
      sourceId: z.string().min(1),
      communityResourceId: z.string().min(1),
      communityUrl: z.string().url(),
      contentHash: z.string().regex(SHA256),
      byteLength: z.number().int().positive(),
      identityNotes: z.string().min(1),
      upstreamDriftUndetectable: z.literal(true),
    })
    .strict(),
  z
    .object({
      kind: z.literal("figma-hosted-version"),
      sourceId: z.string().min(1),
      fileKey: z.string().min(1),
      pinnedVersion: z.string().min(1),
      contentHash: z.string().regex(SHA256),
    })
    .strict(),
]);

export const LocalEvaluationScopeV1Schema = z
  .object({
    kind: z.literal("local-figma-evaluation-scope-v1"),
    scope: z.literal("exact-snapshot"),
    permittedOperations: z
      .array(z.enum(["headless-parse", "local-preview-render"]))
      .min(1)
      .max(2)
      .superRefine((value, ctx) => {
        if (new Set(value).size !== value.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "permittedOperations must be unique",
          });
        }
      }),
    assetEgressAllowed: z.literal(false),
    networkExfiltrationAllowed: z.literal(false),
  })
  .strict();

function sourceErrors(value: Record<string, unknown>, label: string): string[] {
  return sourceIdentitySchema.safeParse(value.source).success
    ? []
    : [`${label} source is not a closed SourceIdentity`];
}

async function sourceAndSelectionErrors(
  value: Record<string, unknown>,
  label: string,
): Promise<string[]> {
  const errors = sourceErrors(value, label);
  const selection = SourceStructureSelectionV1Schema.safeParse(value.selection);
  if (!selection.success)
    return [
      ...errors,
      `${label} selection is not a closed SourceStructureSelectionV1`,
    ];
  const base = {
    kind: selection.data.kind,
    selectionId: selection.data.selectionId,
    members: selection.data.members,
  };
  if (
    selection.data.canonicalSelectionHash !== (await admissionContentHash(base))
  )
    errors.push(`${label} canonical selection hash is internally inconsistent`);
  return errors;
}

async function sameSource(
  left: Record<string, unknown>,
  right: Record<string, unknown>,
  label: string,
): Promise<string[]> {
  const leftSource = sourceIdentitySchema.safeParse(left.source);
  const rightSource = sourceIdentitySchema.safeParse(right.source);
  if (
    !leftSource.success ||
    !rightSource.success ||
    stableAdmissionJson(leftSource.data) !==
      stableAdmissionJson(rightSource.data)
  ) {
    return [`${label} source identity differs`];
  }
  return [];
}

async function sameSourceAndSelection(
  left: Record<string, unknown>,
  right: Record<string, unknown>,
  label: string,
): Promise<string[]> {
  const leftSelection = SourceStructureSelectionV1Schema.safeParse(
      left.selection,
    ),
    rightSelection = SourceStructureSelectionV1Schema.safeParse(
      right.selection,
    );
  const errors: string[] = [];
  errors.push(
    ...(await sourceAndSelectionErrors(left, `${label} left`)),
    ...(await sourceAndSelectionErrors(right, `${label} right`)),
  );
  errors.push(...(await sameSource(left, right, label)));
  if (
    !leftSelection.success ||
    !rightSelection.success ||
    stableAdmissionJson(leftSelection.data) !==
      stableAdmissionJson(rightSelection.data)
  )
    errors.push(`${label} canonical selection differs`);
  return errors;
}

export async function evaluateLocalEvaluationRightsReceiptV2(
  receipt: unknown,
): Promise<ReceiptEvaluation> {
  const value = record(receipt);
  const errors = await hashErrors("local evaluation receipt", receipt);
  if (!value) return { passed: false, errors };
  if (value.schemaVersion !== "cogo.local-evaluation-rights-receipt.v2")
    errors.push("local evaluation receipt has the wrong schemaVersion");
  errors.push(...sourceErrors(value, "local evaluation receipt"));
  if (!LocalEvaluationScopeV1Schema.safeParse(value.evaluationScope).success)
    errors.push("local evaluation receipt has an invalid evaluationScope");
  if ("selection" in value)
    errors.push(
      "local evaluation receipt must not carry a selection before designation",
    );
  const grant = capabilityGrantSchema.safeParse(value.grant);
  if (
    !grant.success ||
    !grant.data.localEvaluation ||
    Object.entries(grant.success ? grant.data : {}).some(
      ([key, allowed]) => key !== "localEvaluation" && allowed,
    )
  )
    errors.push("local evaluation receipt may grant only localEvaluation");
  if (
    !record(value.source) ||
    record(value.source)!.kind !== "local-figma-snapshot"
  )
    errors.push("local evaluation receipt requires a local Figma snapshot");
  if (!attributionSchema.safeParse(value.attributionObligation).success)
    errors.push("local evaluation receipt has invalid attribution");
  return { passed: errors.length === 0, errors };
}

export async function evaluateFigmaQualityDecisionV2(
  proposal: unknown,
  decision: unknown,
): Promise<ReceiptEvaluation> {
  const proposalValue = record(proposal),
    decisionValue = record(decision);
  const errors = [
    ...(await hashErrors("FQ proposal", proposal)),
    ...(await hashErrors("FQ decision", decision)),
  ];
  if (!proposalValue || !decisionValue) return { passed: false, errors };
  if (proposalValue.schemaVersion !== "cogo.figma-quality-evidence-proposal.v2")
    errors.push("FQ proposal has the wrong schemaVersion");
  if (
    decisionValue.schemaVersion !==
    "cogo.figma-quality-confirmation-decision.v2"
  )
    errors.push("FQ decision has the wrong schemaVersion");
  errors.push(
    ...sameRef(
      decisionValue.proposalRef,
      proposalValue.proposalId,
      proposalValue.contentHash,
      "FQ decision proposalRef",
    ),
  );
  if (decisionValue.decision !== "approved")
    errors.push("FQ decision must be approved");
  const actor = record(decisionValue.actor);
  if (!actor || (actor.kind !== "user" && actor.kind !== "organization-admin"))
    errors.push(
      "FQ decision requires a human user or organization-admin actor",
    );
  return { passed: errors.length === 0, errors };
}

export async function evaluateFigmaQualityAdmissionReceiptV2(
  proposal: unknown,
  decision: unknown,
  receipt: unknown,
): Promise<ReceiptEvaluation> {
  const proposalValue = record(proposal),
    decisionValue = record(decision),
    receiptValue = record(receipt);
  const errors = [
    ...(await hashErrors("FQ receipt", receipt)),
    ...(await evaluateFigmaQualityDecisionV2(proposal, decision)).errors,
  ];
  if (!proposalValue || !decisionValue || !receiptValue)
    return { passed: false, errors };
  if (
    receiptValue.schemaVersion !== "cogo.figma-quality-admission-receipt.v2" ||
    receiptValue.lifecycleState !== "adopted" ||
    receiptValue.immutable !== true
  )
    errors.push("FQ receipt must be an immutable adopted receipt");
  errors.push(
    ...sameRef(
      receiptValue.proposalRef,
      proposalValue.proposalId,
      proposalValue.contentHash,
      "FQ receipt proposalRef",
    ),
  );
  errors.push(
    ...sameRef(
      receiptValue.decisionRef,
      decisionValue.decisionId,
      decisionValue.contentHash,
      "FQ receipt decisionRef",
    ),
  );
  errors.push(
    ...(await sameSourceAndSelection(
      proposalValue,
      receiptValue,
      "FQ receipt",
    )),
  );
  return { passed: errors.length === 0, errors };
}

export async function evaluateLibraryReuseAdmissionReceiptV2(
  localRights: unknown,
  proposal: unknown,
  decision: unknown,
  fqReceipt: unknown,
  reuseReceipt: unknown,
): Promise<ReceiptEvaluation> {
  const localValue = record(localRights),
    proposalValue = record(proposal),
    fqValue = record(fqReceipt),
    reuseValue = record(reuseReceipt);
  const errors = [
    ...(await hashErrors("reuse receipt", reuseReceipt)),
    ...(await evaluateLocalEvaluationRightsReceiptV2(localRights)).errors,
    ...(
      await evaluateFigmaQualityAdmissionReceiptV2(
        proposal,
        decision,
        fqReceipt,
      )
    ).errors,
  ];
  if (!localValue || !proposalValue || !fqValue || !reuseValue)
    return { passed: false, errors };
  if (reuseValue.schemaVersion !== "cogo.library-reuse-admission-receipt.v2")
    errors.push("reuse receipt has the wrong schemaVersion");
  if (
    fqValue.schemaVersion !== "cogo.figma-quality-admission-receipt.v2" ||
    fqValue.lifecycleState !== "adopted" ||
    fqValue.immutable !== true
  ) {
    errors.push("reuse receipt requires an immutable adopted FQ receipt");
  }
  errors.push(
    ...sameRef(
      proposalValue.localEvaluationRightsRef,
      localValue.receiptId,
      localValue.contentHash,
      "proposal localEvaluationRightsRef",
    ),
  );
  errors.push(
    ...sameRef(
      reuseValue.localEvaluationRightsRef,
      localValue.receiptId,
      localValue.contentHash,
      "reuse localEvaluationRightsRef",
    ),
  );
  errors.push(
    ...sameRef(
      reuseValue.fqReceiptRef,
      fqValue.receiptId,
      fqValue.contentHash,
      "reuse fqReceiptRef",
    ),
  );
  errors.push(
    ...(await sameSource(localValue, reuseValue, "reuse receipt")),
    ...(await sameSourceAndSelection(fqValue, reuseValue, "reuse receipt")),
  );
  const grant = capabilityGrantSchema.safeParse(reuseValue.grant);
  if (
    !grant.success ||
    !grant.data.localEvaluation ||
    !grant.data.ontologyExtraction
  )
    errors.push(
      "reuse receipt must explicitly grant localEvaluation and ontologyExtraction",
    );
  errors.push(
    ...evaluateAttributionInheritance(
      [localValue.attributionObligation],
      [reuseValue.attributionObligation],
    ).errors,
  );
  return { passed: errors.length === 0, errors };
}

export async function evaluateFigmaSourceRegistrationRecordV2(
  localRights: unknown,
  proposal: unknown,
  decision: unknown,
  fqReceipt: unknown,
  reuseReceipt: unknown,
  registration: unknown,
): Promise<ReceiptEvaluation> {
  const localValue = record(localRights),
    fqValue = record(fqReceipt),
    reuseValue = record(reuseReceipt),
    registrationValue = record(registration);
  const errors = [
    ...(await hashErrors("F0 registration", registration)),
    ...(
      await evaluateLibraryReuseAdmissionReceiptV2(
        localRights,
        proposal,
        decision,
        fqReceipt,
        reuseReceipt,
      )
    ).errors,
  ];
  if (!localValue || !fqValue || !reuseValue || !registrationValue)
    return { passed: false, errors };
  if (
    registrationValue.schemaVersion !==
    "cogo.figma-source-registration-record.v2"
  )
    errors.push("F0 registration has the wrong schemaVersion");
  errors.push(
    ...sameRef(
      registrationValue.localEvaluationRightsRef,
      localValue.receiptId,
      localValue.contentHash,
      "F0 localEvaluationRightsRef",
    ),
  );
  errors.push(
    ...sameRef(
      registrationValue.fqReceiptRef,
      fqValue.receiptId,
      fqValue.contentHash,
      "F0 fqReceiptRef",
    ),
  );
  errors.push(
    ...sameRef(
      registrationValue.libraryReuseReceiptRef,
      reuseValue.receiptId,
      reuseValue.contentHash,
      "F0 libraryReuseReceiptRef",
    ),
  );
  errors.push(
    ...(await sameSource(localValue, registrationValue, "F0 registration")),
    ...(await sameSourceAndSelection(
      fqValue,
      registrationValue,
      "F0 registration",
    )),
    ...(await sameSourceAndSelection(
      reuseValue,
      registrationValue,
      "F0 registration",
    )),
  );
  return { passed: errors.length === 0, errors };
}
